// Tracker de plantilla por correlación cruzada normalizada (NCC) para seguir
// el extremo de la barra olímpica (el disco visto de lateral). La NCC es
// invariante a cambios lineales de brillo/contraste. Coordenadas en la
// resolución del canvas de trabajo.
//
// Dos plantillas por diseño:
//  - ANCLA: la del clic inicial, nunca se modifica. Es la referencia para
//    RECUPERAR el objeto cuando desaparece (sale de cuadro, lo tapa el atleta
//    o el rack) y vuelve a aparecer.
//  - ADAPTATIVA: se refresca poco a poco (blendTemplate) para tolerar el giro
//    del disco y los cambios de luz locales. Es la que sigue el objeto frame a
//    frame, pero puede derivar sobre el oclusor si se usara sola tras una
//    oclusión: por eso la reaquisición usa el ancla.
//
// NOTA: existe una implementación alternativa por Hough circular en
// houghCircleTracker.js (archivada). El coach prefiere los resultados de ésta.

export const TEMPLATE_RADIUS = 21; // plantilla de 43x43 px
export const SEARCH_RADIUS = 48; // ventana de búsqueda en seguimiento normal
export const MIN_SCORE = 0.35; // por debajo, el objeto se da por perdido en ese frame
export const REACQUIRE_SCORE = 0.5; // recuperar exige más certeza que seguir
export const MAX_REACQUIRE_RADIUS = 140; // tope de la ventana de búsqueda al recuperar

function clampInt(v, min, max) {
  return v < min ? min : v > max ? max : v;
}

function finalizeTemplate(t, size, radius) {
  const n = t.length;
  let sum = 0;
  let sum2 = 0;
  for (let i = 0; i < n; i++) {
    sum += t[i];
    sum2 += t[i] * t[i];
  }
  const mean = sum / n;
  const std = Math.sqrt(Math.max(1e-6, sum2 / n - mean * mean));
  return { data: t, size, radius, mean, std };
}

// Extrae la plantilla centrada en (cx, cy) del frame en gris.
export function extractTemplate(gray, cx, cy, radius = TEMPLATE_RADIUS) {
  const size = 2 * radius + 1;
  const t = new Float32Array(size * size);
  const { data, width, height } = gray;
  const icx = Math.round(cx);
  const icy = Math.round(cy);
  let k = 0;
  for (let dy = -radius; dy <= radius; dy++) {
    const y = clampInt(icy + dy, 0, height - 1);
    for (let dx = -radius; dx <= radius; dx++) {
      const x = clampInt(icx + dx, 0, width - 1);
      t[k++] = data[y * width + x];
    }
  }
  return finalizeTemplate(t, size, radius);
}

// NCC de la plantilla centrada en (cx, cy). Devuelve un valor en [-1, 1].
function nccAt(gray, tmpl, cx, cy) {
  const { data: g, width, height } = gray;
  const { data: t, radius, mean: meanT, std: stdT } = tmpl;
  let sumW = 0;
  let sumW2 = 0;
  let sumWT = 0;
  let k = 0;
  for (let dy = -radius; dy <= radius; dy++) {
    const y = clampInt(cy + dy, 0, height - 1);
    const row = y * width;
    for (let dx = -radius; dx <= radius; dx++) {
      const x = clampInt(cx + dx, 0, width - 1);
      const w = g[row + x];
      sumW += w;
      sumW2 += w * w;
      sumWT += w * t[k++];
    }
  }
  const n = t.length;
  const meanW = sumW / n;
  const stdW = Math.sqrt(Math.max(1e-6, sumW2 / n - meanW * meanW));
  return (sumWT / n - meanW * meanT) / (stdW * stdT);
}

// Busca la mejor coincidencia alrededor de (prevX, prevY): pasada gruesa y
// refinado fino alrededor del mejor candidato. Devuelve { x, y, score }.
export function trackStep(gray, tmpl, prevX, prevY, searchRadius = SEARCH_RADIUS, coarseStep = 3) {
  const px = Math.round(prevX);
  const py = Math.round(prevY);
  let best = { x: px, y: py, score: -2 };
  for (let dy = -searchRadius; dy <= searchRadius; dy += coarseStep) {
    for (let dx = -searchRadius; dx <= searchRadius; dx += coarseStep) {
      const s = nccAt(gray, tmpl, px + dx, py + dy);
      if (s > best.score) best = { x: px + dx, y: py + dy, score: s };
    }
  }
  const cx = best.x;
  const cy = best.y;
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      if (dx === 0 && dy === 0) continue;
      const s = nccAt(gray, tmpl, cx + dx, cy + dy);
      if (s > best.score) best = { x: cx + dx, y: cy + dy, score: s };
    }
  }
  return best;
}

// Mezcla la plantilla con el parche actual (in place) para adaptarse a giros
// del disco y cambios de iluminación sin derivar demasiado rápido.
export function blendTemplate(tmpl, gray, cx, cy, alpha = 0.12) {
  const fresh = extractTemplate(gray, cx, cy, tmpl.radius);
  const t = tmpl.data;
  const f = fresh.data;
  for (let i = 0; i < t.length; i++) t[i] = t[i] * (1 - alpha) + f[i] * alpha;
  const upd = finalizeTemplate(t, tmpl.size, tmpl.radius);
  tmpl.mean = upd.mean;
  tmpl.std = upd.std;
}

// Estado del tracker a partir del clic del coach en el disco.
export function createTracker(gray, cx, cy) {
  return {
    anchor: extractTemplate(gray, cx, cy),
    adaptive: extractTemplate(gray, cx, cy),
    x: cx,
    y: cy,
    lost: 0, // frames consecutivos sin encontrar el objeto
  };
}

// Un paso de seguimiento.
// Devuelve { x, y, score, ok, searching }:
//  - ok:true  -> objeto encontrado; añade el punto a la trayectoria.
//  - ok:false -> no encontrado en este frame; NO se añade punto (la línea deja
//    un hueco) y el tracker sigue buscando indefinidamente con una ventana que
//    crece: así el disco se vuelve a enganchar al reaparecer y la trayectoria
//    continúa. Nunca se abandona el seguimiento por sí solo.
export function stepTracker(st, gray) {
  if (st.lost === 0) {
    // Seguimiento normal con la plantilla adaptativa.
    const res = trackStep(gray, st.adaptive, st.x, st.y);
    if (res.score >= MIN_SCORE) {
      st.x = res.x;
      st.y = res.y;
      if (res.score > 0.6) blendTemplate(st.adaptive, gray, res.x, res.y);
      return { x: res.x, y: res.y, score: res.score, ok: true, searching: false };
    }
    st.lost = 1;
    return { x: st.x, y: st.y, score: res.score, ok: false, searching: true };
  }

  // Reaquisición: ventana creciente alrededor de la última posición buena y
  // plantilla ANCLA (la adaptativa puede haberse contaminado con el oclusor).
  // Paso grueso mayor para que la ventana grande siga siendo barata.
  const radius = Math.min(SEARCH_RADIUS + st.lost * 10, MAX_REACQUIRE_RADIUS);
  const withAnchor = trackStep(gray, st.anchor, st.x, st.y, radius, 4);
  const withAdaptive = trackStep(gray, st.adaptive, st.x, st.y, radius, 4);
  const res = withAnchor.score >= withAdaptive.score ? withAnchor : withAdaptive;
  if (res.score >= REACQUIRE_SCORE) {
    st.x = res.x;
    st.y = res.y;
    st.lost = 0;
    // Tras recuperar, la plantilla adaptativa vuelve a partir del ancla para no
    // arrastrar lo que se hubiera aprendido del oclusor.
    st.adaptive = extractTemplate(gray, res.x, res.y);
    return { x: res.x, y: res.y, score: res.score, ok: true, searching: false, reacquired: true };
  }
  st.lost += 1;
  return { x: st.x, y: st.y, score: res.score, ok: false, searching: true };
}
